import { useEffect, useRef } from "react";
import { useMap } from "@vis.gl/react-google-maps";

import type { CarrierTask } from "@/api/carrier";
import { isTaskSelectableForStaging } from "./task-staging";

export function TasksSelector({
  tasks,
  onSelectionComplete,
}: {
  tasks: CarrierTask[];
  onSelectionComplete: (tasks: CarrierTask[]) => void;
}) {
  const map = useMap();
  const latest = useRef({ tasks, onSelectionComplete });
  latest.current = { tasks, onSelectionComplete };

  useEffect(() => {
    if (!map) return;
    const container = map.getDiv();
    if (!container) return;

    const previousCursor = map.get("draggableCursor") as
      | string
      | null
      | undefined;
    map.setOptions({ draggable: false, draggableCursor: "crosshair" });

    const marquee = document.createElement("div");
    marquee.style.cssText =
      "position:absolute;border:2px solid #4f46e5;background:rgba(79,70,229,0.15);" +
      "pointer-events:none;z-index:9999;display:none;will-change:left,top,width,height;";
    container.appendChild(marquee);

    let startLatLng: google.maps.LatLng | null = null;
    let startX = 0;
    let startY = 0;
    let currentX = 0;
    let currentY = 0;
    let dragging = false;
    let frame: number | null = null;

    const draw = () => {
      frame = null;
      marquee.style.left = `${Math.min(startX, currentX)}px`;
      marquee.style.top = `${Math.min(startY, currentY)}px`;
      marquee.style.width = `${Math.abs(currentX - startX)}px`;
      marquee.style.height = `${Math.abs(currentY - startY)}px`;
    };
    const onMove = (event: MouseEvent) => {
      if (!dragging) return;
      const rect = container.getBoundingClientRect();
      currentX = event.clientX - rect.left;
      currentY = event.clientY - rect.top;
      frame ??= requestAnimationFrame(draw);
    };
    const stopVisual = () => {
      dragging = false;
      window.removeEventListener("mousemove", onMove);
      if (frame !== null) cancelAnimationFrame(frame);
      frame = null;
      marquee.style.display = "none";
    };

    const downListener = map.addListener(
      "mousedown",
      (event: google.maps.MapMouseEvent) => {
        const domEvent = event.domEvent as MouseEvent | undefined;
        if (!event.latLng || !domEvent) return;
        const rect = container.getBoundingClientRect();
        startLatLng = event.latLng;
        startX = currentX = domEvent.clientX - rect.left;
        startY = currentY = domEvent.clientY - rect.top;
        dragging = true;
        marquee.style.display = "block";
        draw();
        window.addEventListener("mousemove", onMove);
      }
    );

    const upListener = map.addListener(
      "mouseup",
      (event: google.maps.MapMouseEvent) => {
        if (!startLatLng) return;
        const moved =
          Math.abs(currentX - startX) > 4 || Math.abs(currentY - startY) > 4;
        const endLatLng = event.latLng;
        stopVisual();
        if (endLatLng && moved) {
          const bounds = new google.maps.LatLngBounds();
          bounds.extend(startLatLng);
          bounds.extend(endLatLng);
          const selected = latest.current.tasks.filter((task) => {
            if (!isTaskSelectableForStaging(task) || !task.mapLocation) {
              return false;
            }
            return bounds.contains(task.mapLocation);
          });
          latest.current.onSelectionComplete(selected);
        }
        startLatLng = null;
      }
    );

    const onWindowUp = () => {
      setTimeout(() => {
        if (!dragging) return;
        stopVisual();
        startLatLng = null;
      }, 0);
    };
    window.addEventListener("mouseup", onWindowUp);

    return () => {
      stopVisual();
      window.removeEventListener("mouseup", onWindowUp);
      downListener.remove();
      upListener.remove();
      marquee.remove();
      map.setOptions({
        draggable: true,
        draggableCursor: previousCursor ?? null,
      });
    };
  }, [map]);

  return null;
}
